import { useEffect, useState } from "react";
import { Seo } from "../components/Seo";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { toast } from "../components/ui/sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const initialState = { name: "", email: "", phone: "", company: "", vehicle_info: "", part_sku: "", part_name: "", message: "" };

export default function QuotePage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const sku = searchParams.get("sku");
    if (sku) {
      setForm((f) => ({ ...f, part_sku: sku }));
      api.get(`/parts/${sku}`).then((res) => setForm((f) => ({ ...f, part_name: res.data.name }))).catch(() => {});
    }
  }, [searchParams]);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setSubmitted(true);
      toast.success("Quote request submitted — our team will respond shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="quote-success-state">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <CheckCircle2 className="h-14 w-14 text-amber-light mx-auto mb-6" />
          <h1 className="font-display text-3xl uppercase text-white mb-3">Request Received</h1>
          <p className="text-zinc-400">Our technicians will follow up at {form.email} within 24 hours with pricing and availability.</p>
          <Button onClick={() => { setForm(initialState); setSubmitted(false); }} className="mt-8 bg-amber-light text-obsidian hover:bg-white" data-testid="quote-submit-another-btn">
            Submit Another Request
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian pt-32 pb-24" data-testid="quote-page">
      <Seo
        title="Request a Quote | Orion Services LLC"
        description="Request a parts quote from Orion Services LLC. Provide your vehicle or equipment details and SKU for fast pricing."
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-xs font-mono uppercase tracking-[0.25em] text-amber-light">Order Intake</span>
        <h1 className="font-display text-4xl sm:text-5xl uppercase text-white mt-2 mb-3">Request A Quote</h1>
        <p className="text-zinc-400 mb-10">Fill out the details below. No online payment required — our team follows up directly with pricing and lead-time.</p>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Full Name *</Label>
              <Input required data-testid="quote-form-name" value={form.name} onChange={handleChange("name")} className="!bg-carbon border-amber-500/15" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Email *</Label>
              <Input required type="email" data-testid="quote-form-email" value={form.email} onChange={handleChange("email")} className="!bg-carbon border-amber-500/15" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Phone *</Label>
              <Input required data-testid="quote-form-phone" value={form.phone} onChange={handleChange("phone")} className="!bg-carbon border-amber-500/15" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Company (Optional)</Label>
              <Input data-testid="quote-form-company" value={form.company} onChange={handleChange("company")} className="!bg-carbon border-amber-500/15" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Vehicle / Equipment Details *</Label>
            <Input required placeholder="e.g. Freightliner Cascadia 2021, John Deere 8R" data-testid="quote-form-vehicle" value={form.vehicle_info} onChange={handleChange("vehicle_info")} className="!bg-carbon border-amber-500/15" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Part SKU (Optional)</Label>
              <Input placeholder="e.g. OS-ENG-9042" data-testid="quote-form-part-sku" value={form.part_sku} onChange={handleChange("part_sku")} className="!bg-carbon border-amber-500/15" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Part Name (Optional)</Label>
              <Input data-testid="quote-form-part-name" value={form.part_name} onChange={handleChange("part_name")} className="!bg-carbon border-amber-500/15" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Additional Notes</Label>
            <Textarea rows={4} data-testid="quote-form-notes" value={form.message} onChange={handleChange("message")} className="!bg-carbon border-amber-500/15" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="quote-form-submit" className="w-full bg-amber-light text-obsidian hover:bg-white font-semibold uppercase tracking-wide py-6">
            {submitting ? "Submitting..." : "Submit Quote Request"}
          </Button>
        </form>
      </div>
    </div>
  );
}
